import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ServiceCollectionRecord } from '@common/data-models/service/service-collection-record';
import { ServiceRecord } from '@common/data-models/service/service-record';
import { ServiceResponse } from '@common/data-models/service/service-response';
import { ObjectTypes } from '@common/enums/object-types';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { ViewStateRecordsAlias } from '@common/services/layout-state';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { VisualStateService } from '@common/services/visual-state-service';
import { initializeRecord } from '@src/suite/common/utils/record-utils';
import { DateRange } from '@suite/common/data-models/complex-types/date-range';
import { DateTime } from '@suite/common/data-models/complex-types/date-time';
import { PublicationChannelSchedule } from '@suite/common/data-models/records/publication-channel-record';
import { PublicationViewStateRecord } from '@suite/common/data-models/records/publication-viewstate-record';
import moment from 'moment-timezone';

@Injectable({ providedIn: 'root' })
export class CastSearchService {
    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        public globalTablesService: GlobalTablesService,
        public visualStateService: VisualStateService
    ) {}

    public async getPublicationListsOfRestrictedChannelType(vs: ViewStateRecordsAlias, channelsGuids: Array<string>): Promise<CastSearchResult> {
        const results: CastSearchResult = {
            publicationListsOfRestrictedChannelTypes: [],
        };
        let response: HttpResponse<any> = null;
        const recordRequestParams = this.buildSearchParamsForPublicationListsOfRestrictedChannelType(vs, channelsGuids);
        recordRequestParams.IncludeChildren = true;

        recordRequestParams.FieldsToExclude = ['PSTViewStateScreenshot', 'backfields'];
        try {
            response = await this.nomWebServicesJson.post('/api/v2/Search', recordRequestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to load search results!',
                detailMessage: `Failed to load search results! \n\rStackTrace: NomWebServicesJson.getSearchResults(); \n\rError: ${error}`,
            });
            return null;
        }
        if (response) {
            const respObj = response.body;
            for (const p of respObj.SearchResults) {
                if (p.ListData.Type === ObjectTypes.PUBLICATIONLIST) {
                    const serviceRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(p);
                    serviceRecord.instantiateChildren();
                    const publicationListRecord = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingPublicationListRecord>(serviceRecord);
                    results.publicationListsOfRestrictedChannelTypes.push(publicationListRecord);
                    // DataStore.set(publicationListRecord);
                }
            }
            // results.objectType = ObjectTypes.PUBLICATIONLIST;
            return results;
        }
    }

    public async getSearchResultsV2(vs: ViewStateRecordsAlias, loadSpecificRevision?: boolean): Promise<ServiceResponse<CastSearchResult>> {
        // 1. we need to get the channel guid from vs
        // 2. load publication list and channel data based on channel guid and current date. take latest revision (unpublished)
        // 3. load pub items
        // 4. divide them across sections based on pub list section field (JSON payload)
        // channel service ?
        // navigate prev and next => filter by pub list with that channel guid and that time range
        // revision dropdown
        // PUBLISH button => mark current pub list as published and clear area
        // const results = new CastSearchResults();
        if (!(<PublicationViewStateRecord>vs)?.publicationChannel?.PSTPublicationChannelSchedule) {
            // nothing to do
            return new ServiceResponse<CastSearchResult>(false, null);
        }
        let response: HttpResponse<any> = null;
        const recordRequestParams = this.buildSearchParams(vs, loadSpecificRevision);
        recordRequestParams.IncludeChildren = true;

        recordRequestParams.FieldsToExclude = ['PSTViewStateScreenshot', 'backfields'];
        try {
            response = await this.nomWebServicesJson.post('/api/v2/Search', recordRequestParams, {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: 'Failed to load search results!',
                detailMessage: `Failed to load search results! \n\rStackTrace: NomWebServicesJson.getSearchResults(); \n\rError: ${error}`,
            });
            return new ServiceResponse<CastSearchResult>(false, null);
        }

        if (!response) {
            return new ServiceResponse<CastSearchResult>(false, null);
        }
        const respObj = response.body;
        const results: CastSearchResult = {
            publicationList: null,
            assignments: new Map<string, StorytellingAssignmentRecord>(),
            topics: new Map<string, StorytellingTopicRecord>(),
            listGuids: new Set<string>(),
        };
        for (const p of respObj.SearchResults) {
            if (p.ListData.Type === ObjectTypes.PUBLICATIONLIST) {
                const serviceRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(p);
                serviceRecord.instantiateChildren();
                const publicationListRecord = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingPublicationListRecord>(serviceRecord);
                results.publicationList = publicationListRecord;
                // only process ONE publication list
                break;
            }
        }
        if (results.publicationList !== null) {
            const linkedObjects: Array<string> = new Array<string>();
            const listGuids = new Set<string>();
            // TODO
            // now we need to fetch linked records, if they don't already exist in the data store
            for (const child of results.publicationList.children) {
                const publication: StorytellingPublicationRecord = <StorytellingPublicationRecord>child;
                if (publication?.Relationships && publication.Relationships.getRowCount() > 0) {
                    linkedObjects.push(publication.Relationships.getGuid(0));
                }
                listGuids.add(publication.Guid);
            }
            if (linkedObjects.length > 0) {
                // now fetch linked guids
                const linkedRequestParams = this.buildSequenceParams(linkedObjects);
                linkedRequestParams.FieldsToExclude = ['PSTViewStateScreenshot', 'backfields'];
                let linkedResponse: HttpResponse<any> = null;
                try {
                    linkedResponse = await this.nomWebServicesJson.post('/api/v2/Search', linkedRequestParams, {
                        headers: new HttpHeaders({
                            'Content-Type': 'application/json',
                            'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                        }),
                        responseType: 'json',
                        observe: 'response',
                    });
                } catch (error) {
                    this.nomWebServicesJson.showErrorModal({
                        message: 'Failed to load search results!',
                        detailMessage: `Failed to load search results! \n\rStackTrace: NomWebServicesJson.getSearchResults(); \n\rError: ${error}`,
                    });
                    return new ServiceResponse<CastSearchResult>(false, results);
                }
                const topicResults = new Map<string, StorytellingTopicRecord>();
                const assignmentResults = new Map<string, StorytellingAssignmentRecord>();
                // todo Populate this with sequance results
                if (linkedResponse) {
                    const linkedRespObj = linkedResponse.body;
                    let i = 0;
                    for (const p of linkedRespObj.SearchResults) {
                        i++;
                        if (i === linkedRespObj.SearchResults.length && i > linkedRespObj.MaxResultsReturned) continue;
                        if (p.ListData.Type === 137) {
                            // 1. add Topic results
                            // TODO: add topic record
                            const serviceCollectionRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(p);
                            const topicRecord = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingTopicRecord>(serviceCollectionRecord);
                            topicResults.set(topicRecord.Guid, topicRecord);
                            for (const childScript of p.CollectionScripts) {
                                const assignmentRecord = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingAssignmentRecord>(childScript);
                                assignmentResults.set(assignmentRecord.Guid, assignmentRecord);
                            }
                        }
                        if (p.ListData.Type === 138) {
                            // 2. assignment results - that may or may not have topics
                            const serviceRecord: ServiceRecord = ServiceRecord.instance(p);
                            const assignmentRecord = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingAssignmentRecord>(serviceRecord);
                            const parentRec: StorytellingTopicRecord = initializeRecord(ObjectTypes.TOPIC);
                            parentRec.Guid = assignmentRecord.ParentGuid;
                            parentRec.PSTCategory = assignmentRecord.ParentCategory;
                            parentRec.PSTTopicType = assignmentRecord.ParentType;
                            parentRec.PSTTopicOwner = assignmentRecord.ParentOwner;
                            parentRec.PSTTopicAddress = assignmentRecord.ParentLocation;
                            parentRec.Title = assignmentRecord.ContainerTitle;
                            parentRec.Database = assignmentRecord.Database;
                            parentRec.Path = assignmentRecord.Path.parentCollectionPath;
                            if (!topicResults.has(parentRec.Guid)) {
                                topicResults.set(parentRec.Guid, parentRec);
                            }
                            assignmentResults.set(assignmentRecord.Guid, assignmentRecord);
                        }
                    }
                }

                results.topics = topicResults;
                results.assignments = assignmentResults;
                results.listGuids = listGuids;
            }
        }
        return new ServiceResponse<CastSearchResult>(true, results);
    }

    // public async getSearchResults(vs: ViewStateRecordsAlias): Promise<ServiceResponse<CastSearchResults>> {
    //     // 1. we need to get the channel guid from vs
    //     // 2. load publication list and channel data based on channel guid and current date. take latest revision (unpublished)
    //     // 3. load pub items
    //     // 4. divide them across sections based on pub list section field (JSON payload)
    //     // channel service ?
    //     // navigate prev and next => filter by pub list with that channel guid and that time range
    //     // revision dropdown
    //     // PUBLISH button => mark current pub list as published and clear area
    //     const results = new CastSearchResults();
    //     if (!(<PublicationViewStateRecord>vs)?.publicationChannel?.PSTPublicationChannelSchedule) {
    //         // nothing to do
    //         return new ServiceResponse<CastSearchResults>(false, results);
    //     }
    //     let response: HttpResponse<any> = null;
    //     const recordRequestParams = this.buildSearchParams(vs);
    //     recordRequestParams.IncludeChildren = true;

    //     recordRequestParams.FieldsToExclude = ['PSTViewStateScreenshot', 'backfields'];
    //     try {
    //         response = await this.nomWebServicesJson.post('/api/v2/Search', recordRequestParams, {
    //             headers: new HttpHeaders({
    //                 'Content-Type': 'application/json',
    //                 'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
    //             }),
    //             responseType: 'json',
    //             observe: 'response',
    //         });
    //     } catch (error) {
    //         this.nomWebServicesJson.showErrorModal({
    //             message: 'Failed to load search results!',
    //             detailMessage: `Failed to load search results! \n\rStackTrace: NomWebServicesJson.getSearchResults(); \n\rError: ${error}`,
    //         });
    //         return new ServiceResponse<CastSearchResults>(false, results);
    //     }
    //     if (response) {
    //         const respObj = response.body;
    //         for (const p of respObj.SearchResults) {
    //             if (p.ListData.Type === ObjectTypes.PUBLICATIONLIST) {
    //                 const serviceRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(p);
    //                 serviceRecord.instantiateChildren();
    //                 const publicationListRecord = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingPublicationListRecord>(serviceRecord);
    //                 // await DataStore.setCollection(publicationListRecord, true);
    //                 results.publicationList = publicationListRecord;
    //                 // only process ONE publication list
    //                 break;
    //             }
    //         }
    //         if (results.publicationList !== null) {
    //             const linkedObjects: Array<string> = new Array<string>();
    //             // TODO
    //             // now we need to fetch linked records, if they don't already exist in the data store
    //             // for (let i = 0; i < results.publicationList.getChildren().length; i++) {
    //             //     const publication: StorytellingPublicationRecord = <StorytellingPublicationRecord>results.publicationList.getChildren()[i];
    //             //     if (publication.relationships && publication.relationships.getRowCount() > 0) {
    //             //         linkedObjects.push(publication.relationships.getGuid(0));
    //             //     }
    //             // }
    //             if (linkedObjects.length > 0) {
    //                 // now fetch linked guids
    //                 const linkedRequestParams = this.buildSequenceParams(linkedObjects);
    //                 linkedRequestParams.FieldsToExclude = ['PSTViewStateScreenshot', 'backfields'];
    //                 let linkedResponse: HttpResponse<any> = null;
    //                 try {
    //                     linkedResponse = await this.nomWebServicesJson.post('/api/v2/Search', linkedRequestParams, {
    //                         headers: new HttpHeaders({
    //                             'Content-Type': 'application/json',
    //                             'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
    //                         }),
    //                         responseType: 'json',
    //                         observe: 'response',
    //                     });
    //                 } catch (error) {
    //                     this.nomWebServicesJson.showErrorModal({
    //                         message: 'Failed to load search results!',
    //                         detailMessage: `Failed to load search results! \n\rStackTrace: NomWebServicesJson.getSearchResults(); \n\rError: ${error}`,
    //                     });
    //                     return new ServiceResponse<CastSearchResults>(false, results);
    //                 }
    //                 if (linkedResponse) {
    //                     const linkedRespObj = linkedResponse.body;
    //                     let i = 0;
    //                     for (const p of linkedRespObj.SearchResults) {
    //                         i++;
    //                         if (i === linkedRespObj.SearchResults.length && i > linkedRespObj.MaxResultsReturned) continue;
    //                         if (p.ListData.Type === 137) {
    //                             // 1. add Topic results
    //                             // TODO: add topic record
    //                             const serviceCollectionRecord: ServiceCollectionRecord = ServiceCollectionRecord.instance(p);
    //                             const _topicRecord = this.nomWebServicesJson.convertor.fromServiceCollectionRecordV2<StorytellingTopicRecord>(serviceCollectionRecord);
    //                             // await DataStore.setCollection(topicRecord, true);
    //                             // const record = DataStore.byGuid<TopicRecord>(topicRecord.guid, '');
    //                             // record.setChildren([]);
    //                             for (const childScript of p.CollectionScripts) {
    //                                 const _assignmentRecord = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingAssignmentRecord>(childScript);
    //                                 // DataStore.set(assignmentRecord);
    //                                 // const assignment = DataStore.byGuid<StorytellingAssignmentRecord>(assignmentRecord.guid, '');
    //                                 // if (record && !record.hasChildWithGuid(assignment.guid)) {
    //                                 //     record.unshiftChildren(assignment);
    //                                 // } else {
    //                                 //     record.registerChildHooks(assignment);
    //                                 // }
    //                             }
    //                         }
    //                         if (p.ListData.Type === 138) {
    //                             // 2. assignment results - that may or may not have topics
    //                             const serviceRecord: ServiceRecord = ServiceRecord.instance(p);
    //                             const assignmentRecord = this.nomWebServicesJson.convertor.fromServiceRecordV2<StorytellingAssignmentRecord>(serviceRecord);
    //                             // TODO
    //                             //DataStore.byGuid<TopicRecord>(assignmentRecord.parentGuid, '');
    //                             let parentRec: StorytellingTopicRecord = null;
    //                             if (!parentRec) {
    //                                 parentRec = initializeRecord(ObjectTypes.TOPIC);
    //                             }
    //                             parentRec.Guid = assignmentRecord.ParentGuid;
    //                             parentRec.PSTCategory = assignmentRecord.ParentCategory;
    //                             parentRec.PSTTopicType = assignmentRecord.ParentType;
    //                             parentRec.PSTTopicOwner = assignmentRecord.ParentOwner;
    //                             parentRec.PSTTopicAddress = assignmentRecord.ParentLocation;
    //                             parentRec.Title = p.ListData.ContainerTitle;
    //                             // await DataStore.setCollection(parentRec, true);
    //                             // if (parentRec && !parentRec.hasChildWithGuid(assignmentRecord.guid)) {
    //                             //     parentRec.unshiftChildren(assignmentRecord);
    //                             // } else {
    //                             //     parentRec.registerChildHooks(assignmentRecord);
    //                             // }
    //                             // DataStore.set(assignmentRecord);
    //                         }
    //                     }
    //                 }
    //             }
    //         }
    //     }
    //     results.objectType = ObjectTypes.PUBLICATION;
    //     return new ServiceResponse<CastSearchResults>(true, results);
    // }

    private buildGenericSearchReqParams(maxRows: number = 200, includeChildren: boolean = false): any {
        maxRows = maxRows < 200 ? 200 : maxRows;
        return {
            Database: this.nomWebServicesJson.PSTServer,
            QueryTerms: '',
            zFields: null,
            NOMContentTypes: {
                All: true,
                Audio: false,
                Contacts: false,
                Grids: false,
                Messages: false,
                NewsgatheringGrids: false,
                NewsgatheringItems: false,
                Pictures: false,
                Readins: false,
                Rundowns: false,
                Scripts: false,
                Storyboards: false,
                Video: false,
            },
            NOMContentDates: {
                All: true,
                CustomDateRange: false,
                FortyEightHours: false,
                Future: false,
                ItemDateFrom: '1800-02-28T22:00:00.000Z',
                ItemDateTo: '1800-02-28T22:00:00.000Z',
                OneHour: false,
                OneWeek: false,
                TwentyFourHours: false,
                TwoHours: false,
            },
            WireSearchParameters: {
                SearchWires: false,
                Categories: [],
                Providers: [],
                DateOptions: {
                    All: true,
                    CustomDateRange: false,
                    FortyEightHours: false,
                    Future: false,
                    ItemDateFrom: '1800-02-28T22:00:00.000Z',
                    ItemDateTo: '1800-02-28T22:00:00.000Z',
                    OneHour: false,
                    OneWeek: false,
                    TwentyFourHours: false,
                    TwoHours: false,
                },
                ExtendedWireBriefing: true,
            },
            MaxRows: maxRows,
            SortByRank: false,
            SortFields: null,
            ExactMatch: false,
            IncludeChildren: includeChildren,
        };
    }

    public calculateNgTimeOffset(date: Date) {
        const offset = this.visualStateService.ConfigSettings.NGTimeOffset;
        if (offset) {
            const chunks = offset.split(':');
            const hours = parseInt(chunks[0]);
            const minutes = parseInt(chunks[1]);
            date.setHours(hours);
            date.setMinutes(minutes);
        } else {
            date.setHours(12);
            date.setMinutes(0);
        }
    }

    protected buildSearchParams(vs: ViewStateRecordsAlias, loadSpecificRevision?: boolean): any {
        const zFields = new Array<any>();
        zFields.push({
            FieldName: 'PSTPublicationChannelGUID',
            FieldValue: (<PublicationViewStateRecord>vs).publicationChannelGUID,
        });
        const range: DateRange = new DateRange();
        let time: DateTime = (<PublicationViewStateRecord>vs).publicationTime;
        if (!time || !time.get()) {
            const dt = new DateTime();
            const d = new Date();
            d.setHours(0, 0, 0, 0);
            dt.set(d);
            (<PublicationViewStateRecord>vs).publicationTime = dt;
            time = dt;
        }
        this.calculateNgTimeOffset(time.get());
        const channelRange: PublicationChannelSchedule = (<PublicationViewStateRecord>vs).publicationChannel.PSTPublicationChannelSchedule;
        if (channelRange.toUpperCase() === 'DAILY') {
            range.from = moment(time.get()).subtract(2, 'hours').toDate();
            range.to = moment(time.get()).add(2, 'hours').toDate();
        } else {
            // not supported for now
            throw new Error(`Range ${channelRange} is not supported.`);
        }
        // construct actual search range based on current time in UI as well as Channel's range value, which can be: daily, hourly, weekly, monthly, etc.
        const rangeVal: string = `${moment(range.from).format('YYYY-MM-DDTHH:mm:ss.000')}Z` + `;${moment(range.to).format('YYYY-MM-DDTHH:mm:ss.000')}Z`;

        // construct date range zfield filter
        zFields.push({
            FieldName: 'PSTPublicationListTime__range__',
            FieldValue: rangeVal,
        });

        zFields.push({
            FieldName: 'PSTPublicationEditionName',
            FieldValue: (<PublicationViewStateRecord>vs).editionName,
        });
        // construct revision. default (if not set) should be max => latest rev. that's why we are sorting by desc. also, in this case
        // we should only return 1 list
        const params = this.buildGenericSearchReqParams();
        if (loadSpecificRevision && (<PublicationViewStateRecord>vs).publicationRevisionNumber >= 0) {
            zFields.push({
                FieldName: 'PSTPublicationListRevisionNumber',
                FieldValue: (<PublicationViewStateRecord>vs).publicationRevisionNumber,
            });
        } else {
            params.SortFields = [{ FieldName: 'PSTPublicationListRevisionNumber', FieldValue: 'desc' }];
        }
        // params.SortFields = [{ FieldName: 'PSTPublicationListRevisionNumber', FieldValue: 'desc' }];
        params.MaxRows = 1;
        params.MaxChildRows = 200;
        params.zFields = zFields;
        return params;
    }

    protected buildSequenceParams(sequence: Array<string>): any {
        const zFields = new Array<any>();
        zFields.push({
            FieldName: 'guid__list__',
            FieldValue: sequence.join(';'),
        });
        const params = this.buildGenericSearchReqParams(sequence.length, false);
        params.zFields = zFields;
        return params;
    }

    protected buildSearchParamsForPublicationListsOfRestrictedChannelType(vs: ViewStateRecordsAlias, channelsGuids: Array<string>): any {
        const zFields = new Array<any>();
        zFields.push({
            FieldName: 'PSTPublicationChannelGUID__list__',
            FieldValue: channelsGuids.join(';'),
        });
        zFields.push({
            FieldName: 'type',
            FieldValue: ObjectTypes.PUBLICATIONLIST,
        });
        const params = this.buildGenericSearchReqParams();
        params.zFields = zFields;
        return params;
    }
}
